import { Component, OnInit, OnDestroy } from '@angular/core';
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../../common/utils/froala.utils';
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
import { AuthService } from "../../../../services/auth.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { NoteService } from "../../../../common/services/note.service";
import { UserIdentity } from "../../../../models/user-identity.model";
import { genId } from "../../../../core/services/genId";
import { Note } from "../../../../common/models/note.model";
import { NoteRelatedItemType } from "../../../../common/models/note_relatedItemType.enum";
import { NoteType } from "../../../../common/models/note-type.enum";
import { RetentionPolicy } from "../../../../common/models/retention-policy.model";
import { SidePanelBase } from '../../../../common/components/side-panel-base.class';

@Component({
    selector: 'contact-notes',
    templateUrl: './contact-notes.component.html',
    styleUrls: ['../../../../common/components/notes/notes.component.scss'],
    standalone: false
})
export class ContactNotesComponent extends SidePanelBase implements OnInit, OnDestroy {
    title: string = 'Add Contact Notes';

    constructor(
        public noteService: NoteService,
        public loader: LoaderService,
        public authService: AuthService
    ) { 
        super();

    }

    static componentName = 'ContactNotesComponent';

    form: FormGroup;

    private _froalaOptions: any = null;
    get froalaOptions(): any {
        if (!this._froalaOptions) {
            let key = this.editorKey;
            this._froalaOptions = applyFroalaAriaFix({
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: key,
                typingTimer: 150,
                saveInterval: 0,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough']
            });
        }
        return this._froalaOptions;
    };

    noteType = NoteType;
    subscription: Subscription = new Subscription();
    types: any[] = ["General", "Administrative"];
    private _user: UserIdentity = null;
    _contactNotes: Note[];

    ngOnInit() {
        this.initializeForm();

        if (this.componentData.title)
            this.title = this.componentData.title;

        //To start with the notes come back with the org, so we can get the initial set, we do want to refetch them separately afterwards to make sure they're up do date though.
        if (this.componentData.data.notes)
            this._contactNotes = this.componentData.data.notes;

        //Fetch the updated notes tied to the org

        let obs$ = this.authService.loggedInUser$.pipe(
            tap((user: UserIdentity) => {
                this._user = user;
                if (this._user.isInternal) this.types.push("Internal");
            }),
            switchMap((user: UserIdentity) => {
                return this.noteService.getNotes(this.componentData.data.id, this.componentData.data.noteType || NoteRelatedItemType.Contact).pipe(
                    tap((notes: Note[]) => {
                        this._contactNotes = (this._user.isInternal)
                            ? notes
                            : this.filterInternalNotes(notes);
                    })
                )
            })
        );

        this.subscription = obs$.subscribe(
            () => { console.log("notes loaded"); },
            (error) => { console.error(error); }
        );
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    filterInternalNotes(notes: Note[]): Note[] {
        return notes.filter(note => note.type !== NoteType.Internal);
    }

    cancel(): void {
        this.resetForm();
    }

    openReplyForm(form: any): void {
        form.isReplying = true
        form.replyText = '';
    }

    get contactNotes() {
        return this._contactNotes;
    }

    reply(parentNote: Note, form: any): void {

        this.loader.toggleLoader(true);
        let reply = this.buildNote(form.replyText, parentNote.type);
        reply.text = form.replyText;
        reply.type = parentNote.type;
        reply.parentNoteId = parentNote.id;
        reply.referenceUri = parentNote.referenceUri;
        parentNote.notes.push(reply);

        const index = this._contactNotes.findIndex(x => x.id === parentNote.id);
        form.opened = true;
        this.contactNotes[index] = parentNote;

        this.noteService.postNote(reply).subscribe(note => {
            this.loader.toggleLoader(false);
            this.resetReplyForm(form);
        });
    }

    resetReplyForm(form: any): void {
        form.replyText = '';
        form.isReplying = false;
        form.isDirty = false
    }

    save(): void {
        this.loader.toggleLoader(true);
        const note = this.buildNote(stripFroalaWatermark(this.form.controls['noteText'].value), parseInt(this.form.controls['selectedNoteType'].value));
        note.referenceUri = window.location.href;
        

        this.noteService.postNote(note).subscribe(note => {
            this.loader.toggleLoader(false);
            this.contactNotes.unshift(note);
            this.resetForm();
        });
    }

    buildNote(text: string, type: number): Note {
        return {
            createdBy: this.authService.User.displayName,
            createdById: this.authService.User.id,
            createdDate: new Date(),
            documentType: 'Note',
            relatedItemId: this.componentData.data.id,
            relatedItemType: this.componentData.data.noteType || NoteRelatedItemType.Contact,
            files: [],
            id: genId(),
            isDeleted: false,
            noteId: genId(),
            notes: [],
            priority: 3,
            referenceUri: '',
            retentionPolicy: {} as RetentionPolicy,
            text: text,
            type: type,
            noteUrgency: 0,
        } as Note;
    }

    initializeForm(): void {
        let formControls = {
            selectedNoteType: new FormControl(-1, [Validators.required, Validators.min(0)]),
            noteText: new FormControl('', [Validators.required])
        };
        this.form = new FormGroup(formControls);
    }

    resetForm(): void {
        this.form.reset({ selectedNoteType: -1, noteText: '' });
        this.form.markAsPristine();
        this.form.markAsUntouched();
    }

}
